/**
 * MarqueeManager Component
 * 管理跑馬燈列表，支援拖曳排序功能
 */

import { useState } from 'react';
import type { MarqueeAdmin, MarqueeOrderItem } from '../../types';
import MarqueePreview from './MarqueePreview';
import './MarqueeManager.css';

interface MarqueeManagerProps {
  marquees: MarqueeAdmin[];
  onEdit: (marquee: MarqueeAdmin) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
  onReorder: (orders: MarqueeOrderItem[]) => Promise<void>;
  defaultSpeed?: number;
}

interface DragItem {
  id: string;
  displayOrder: number;
  index: number;
}

const MarqueeManager = ({
  marquees,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
  defaultSpeed = 15,
}: MarqueeManagerProps) => {
  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [reorderError, setReorderError] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, item: MarqueeAdmin, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
    setDraggedItem({ id: item.id, displayOrder: item.displayOrder, index });
    e.currentTarget.classList.add('marquee-manager__item--dragging');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // 設置拖曳時的樣式
    const target = e.currentTarget;
    target.classList.add('marquee-manager__item--drag-over');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove('marquee-manager__item--drag-over');
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetItem: MarqueeAdmin, targetIndex: number) => {
    e.preventDefault();
    e.currentTarget.classList.remove('marquee-manager__item--drag-over');

    if (!draggedItem || draggedItem.id === targetItem.id) {
      return;
    }

    // 移除所有拖曳相關的樣式類
    document.querySelectorAll('.marquee-manager__item--dragging').forEach(el => {
      el.classList.remove('marquee-manager__item--dragging');
    });

    // 先排序，再進行拖曳操作
    const sortedItems = [...marquees].sort((a, b) => a.displayOrder - b.displayOrder);

    // 使用 ID 查找當前實際位置，而不是依賴拖曳時的 index
    const draggedIndex = sortedItems.findIndex(item => item.id === draggedItem.id);
    if (draggedIndex === -1) {
      console.error('❌ [MarqueeManager] Dragged item not found:', draggedItem.id);
      return;
    }

    // Debug: 顯示拖曳資訊
    console.log('🖱️ [MarqueeManager] Drag info:', {
      draggedId: draggedItem.id,
      draggedIndex,
      targetId: targetItem.id,
      targetIndex,
    });

    // 移除拖曳的項目
    const [removed] = sortedItems.splice(draggedIndex, 1);

    // 直接插入到目標位置（拖曳到某個項目 = 插入到該項目的位置）
    sortedItems.splice(targetIndex, 0, removed);

    // 更新 displayOrder
    const reorderedItems = sortedItems.map((item, index) => ({
      id: item.id,
      displayOrder: index + 1, // 從 1 開始
    }));

    // Debug: handleDrop 時 dump 重新排序後的結果
    console.log('🔄 [MarqueeManager] handleDrop - Reordered items:', reorderedItems.map(item => ({
      id: item.id,
      displayOrder: item.displayOrder,
      content: sortedItems.find(i => i.id === item.id)?.content,
    })));

    // 執行樂觀更新：立即清理拖曳狀態，父元件會立即更新顯示
    setDraggedItem(null);
    setReorderError(null);

    // 背景更新 API，失敗時才顯示錯誤
    onReorder(reorderedItems).catch((error) => {
      setReorderError(error.message || '排序失敗，已回復原順序');
    });
  };

  const handleEdit = (marquee: MarqueeAdmin) => {
    onEdit(marquee);
  };

  const handleDelete = (id: string) => {
    if (confirm('確定要刪除此跑馬燈嗎？')) {
      onDelete(id);
    }
  };

  const handleToggle = (id: string) => {
    onToggle(id);
  };

  const getStatusClass = (isActive: boolean) => {
    return isActive ? 'marquee-manager__status--active' : 'marquee-manager__status--inactive';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? '啟用' : '停用';
  };

  // 按照順序排序跑馬燈
  const sortedMarquees = [...marquees].sort((a, b) => a.displayOrder - b.displayOrder);

  // Debug: 載入時 dump 跑馬燈文字訊息
  console.log('📋 [MarqueeManager] Loaded marquees:', sortedMarquees.map(m => ({
    id: m.id,
    displayOrder: m.displayOrder,
    content: m.content,
  })));

  return (
    <div className="marquee-manager">
      <div className="marquee-manager__header">
        <h2 className="marquee-manager__title">跑馬燈管理</h2>
        <div className="marquee-manager__count">
          共 {marquees.length} 條跑馬燈
        </div>
      </div>

      {reorderError && (
        <div className="marquee-manager__error">
          <p>⚠️ {reorderError}</p>
        </div>
      )}

      {sortedMarquees.length === 0 ? (
        <div className="marquee-manager__empty">
          沒有跑馬燈資料
        </div>
      ) : (
        <div className="marquee-manager__list">
          {sortedMarquees.map((marquee, index) => (
            <div
              key={marquee.id}
              draggable
              onDragStart={(e) => handleDragStart(e, marquee, index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, marquee, index)}
              className="marquee-manager__item"
            >
              <div className="marquee-manager__item-header">
                <div className="marquee-manager__item-handle">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 12h16M4 6h16M4 18h16"/>
                  </svg>
                </div>
                <div className="marquee-manager__item-order">
                  <span className="marquee-manager__order-number">
                    {marquee.displayOrder}
                  </span>
                </div>
                <div className="marquee-manager__item-preview">
                  <MarqueePreview
                    content={marquee.content}
                    textColor={marquee.textColor}
                    bgColor={marquee.bgColor}
                    bgOpacity={marquee.bgOpacity}
                    defaultSpeed={defaultSpeed}
                  />
                </div>
                <div className="marquee-manager__item-status">
                  <span className={`marquee-manager__status ${getStatusClass(marquee.isActive)}`}>
                    {getStatusText(marquee.isActive)}
                  </span>
                </div>
                <div className="marquee-manager__item-actions">
                  <button
                    onClick={() => handleEdit(marquee)}
                    className="btn btn--sm"
                    title="編輯"
                  >
                    編輯
                  </button>
                  <button
                    onClick={() => handleToggle(marquee.id)}
                    className={`btn btn--sm ${marquee.isActive ? 'btn--warning' : 'btn--success'}`}
                  >
                    {marquee.isActive ? '停用' : '啟用'}
                  </button>
                  <button
                    onClick={() => handleDelete(marquee.id)}
                    className="btn btn--sm btn--danger"
                    title="刪除"
                  >
                    刪除
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {sortedMarquees.length > 0 && (
        <div className="marquee-manager__help">
          <p>💡 提示：拖曳項目來重新排序順序</p>
        </div>
      )}
    </div>
  );
};

export default MarqueeManager;